/**
 * L4 watchdog primitives — pure overdue predicate + module-let probe map.
 *
 * Mirrors Apple `JobProgressState+Watchdog.swift` and Android
 * `JobQueueRepository.runWatchdogTick`. See WEB-L4 in
 * docs/REALTIME_RECONCILE_PLAN.md.
 *
 * L4 is the primary delivery-safety mechanism — WS-event loss is common
 * (tab suspend, network swap, leader-election flap, Ably gap), and L1/L2/L3
 * are user-action driven. The tick is tight enough that a missed
 * `renders/completed` is recovered in single-digit seconds.
 */

import { RenderStatus } from '@/shared/constants';
import { isRenderInQueue } from '@/shared/lib/render-status';

import type { Job } from '../model/job-queue.types';

export const WATCHDOG_TICK_INTERVAL_MS = 10_000;
export const WATCHDOG_REPROBE_WINDOW_MS = 10_000;
export const WATCHDOG_DURATION_MULTIPLIER = 1.0;
export const WATCHDOG_GRACE_MS = 10_000;

/**
 * Per-jobType fallback predicted duration (seconds) used when the job row
 * is missing `durationPredicted`. Album renders legitimately take 4–7 min;
 * the render-tier fallback stays short so a stuck render flips into probe
 * quickly, but the album-tier fallback is long enough not to probe-storm
 * a normal album.
 */
export const WATCHDOG_FALLBACK_DURATION_RENDER_SEC = 60;
export const WATCHDOG_FALLBACK_DURATION_ALBUM_SEC = 300;

/**
 * Per-job timestamp of the most recent probe (wall-clock ms). Module-let
 * for the same reason as `reconcile-throttle` / `realtime-sync.locks`: the
 * watchdog timer lives in `useEffect`, but probe coordination must outlive
 * Strict-Mode double-mounts and survive provider unmount.
 */
const lastProbeAt = new Map<string, number>();

/**
 * Mark a probe as fired BEFORE the API await. A slow `getRender` request
 * must not lead to a duplicate probe on the next 30s tick.
 */
export function markWatchdogProbeFired(
  jobId: string,
  now: number = Date.now()
): void {
  lastProbeAt.set(jobId, now);
}

export function shouldProbeJobNow(
  jobId: string,
  now: number = Date.now()
): boolean {
  const last = lastProbeAt.get(jobId) ?? 0;
  return now - last >= WATCHDOG_REPROBE_WINDOW_MS;
}

export function clearWatchdogProbeFor(jobId: string): void {
  lastProbeAt.delete(jobId);
}

export function resetWatchdogProbeMap(): void {
  lastProbeAt.clear();
}

/**
 * Pure overdue predicate. Two branches:
 *
 * - **pending** — server hasn't said "started" yet. Deadline =
 *   `createdAt + WATCHDOG_GRACE_MS`. After this many ms in queue we probe
 *   so that a lost `renders/started` / `albums/started` doesn't strand the
 *   row on "queued" forever.
 *
 * - **processing** — server is running. Deadline =
 *   `(startedAt ?? createdAt) + predicted * multiplier + grace`. Albums get
 *   a longer fallback when `durationPredicted` is missing so they don't
 *   probe-storm while legitimately producing.
 *
 * Synthetic stub rows (no `createdAt` and no `startedAt`) are skipped — we
 * can't compute a deadline without a timestamp.
 */
export function isJobOverdue(job: Job, now: number = Date.now()): boolean {
  if (!isRenderInQueue(job.status)) return false;

  if (job.status === RenderStatus.PENDING) {
    const createdMs = job.createdAt ? Date.parse(job.createdAt) : NaN;
    if (Number.isNaN(createdMs)) return false;
    return now > createdMs + WATCHDOG_GRACE_MS;
  }

  if (job.status === RenderStatus.PROCESSING) {
    const reference = job.startedAt ?? job.createdAt;
    if (!reference) return false;
    const referenceMs = Date.parse(reference);
    if (Number.isNaN(referenceMs)) return false;

    const fallback =
      job.jobType === 'album'
        ? WATCHDOG_FALLBACK_DURATION_ALBUM_SEC
        : WATCHDOG_FALLBACK_DURATION_RENDER_SEC;
    const predictedSec = job.durationPredicted || fallback;
    const deadlineMs =
      referenceMs +
      predictedSec * WATCHDOG_DURATION_MULTIPLIER * 1000 +
      WATCHDOG_GRACE_MS;

    return now > deadlineMs;
  }

  return false;
}

/**
 * Selects jobs that should be probed on this tick: overdue AND outside
 * the per-job re-probe window. Used by the watchdog hook AND by tests.
 */
export function selectJobsToProbe(jobs: Job[], now: number = Date.now()): Job[] {
  return jobs.filter(
    (job) => isJobOverdue(job, now) && shouldProbeJobNow(job.id, now)
  );
}
